import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OutletPerformanceRow } from '../types';
import { Icon } from './shared/Icon';

export type HeroSignalTone = 'win' | 'risk' | 'watch';

export type HeroSignal = {
  tone: HeroSignalTone;
  icon:
    | 'trendUp'
    | 'flag'
    | 'activity'
    | 'pin'
    | 'users'
    | 'x'
    | 'table'
    | 'check';
  label: string;
  value: string;
  detail: string;
  action: string;
  priority: number;
  outletId?: string;
  /** True for open/close notes — excluded from the headline verdict counts. */
  structural?: boolean;
  patternKey?: string;
};

/** Bold every occurrence of an outlet name inside hero card copy. */
export function highlightOutletName(
  text: string,
  outletName: string | undefined,
): React.ReactNode {
  if (!outletName || !text.includes(outletName)) return text;
  const segments = text.split(outletName);
  return segments.map((segment, index) => (
    <React.Fragment key={index}>
      {segment}
      {index < segments.length - 1 ? (
        <b className="ph-outlet-name">{outletName}</b>
      ) : null}
    </React.Fragment>
  ));
}

/** Dense outlet exception row for the hero rail (not a nested card stack). */
export function OutletHeroSignalCard({
  signal,
  onSelect,
  outletRows,
}: {
  signal: HeroSignal;
  onSelect?: (outletId: string) => void;
  outletRows: OutletPerformanceRow[];
}) {
  const clickable = Boolean(signal.outletId && onSelect);
  const outletName = signal.outletId
    ? outletRows.find(r => r.outlet.id === signal.outletId)?.outlet.name
    : undefined;
  const tip = [signal.detail, signal.action].filter(Boolean).join(' · ');

  const activate = () => {
    if (signal.outletId && onSelect) onSelect(signal.outletId);
  };

  return (
    <div
      className={`ph-outlet ${signal.tone}${clickable ? ' is-clickable' : ''}`}
      title={tip || undefined}
      onClick={clickable ? activate : undefined}
      onKeyDown={
        clickable
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                activate();
              }
            }
          : undefined
      }
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <span className="ph-outlet-ic" aria-hidden>
        <Icon name={signal.icon} size={13} />
      </span>
      <div className="ph-outlet-copy">
        <div className="ph-outlet-lab">
          {highlightOutletName(signal.label, outletName)}
        </div>
        {signal.detail ? (
          <div className="ph-outlet-detail">
            {highlightOutletName(signal.detail, outletName)}
          </div>
        ) : null}
      </div>
      <div className="ph-outlet-val">{signal.value}</div>
    </div>
  );
}

export function MetricInsightTile({ tile }: { tile: HeroSignal }) {
  const { t } = useTranslation('performance');
  const [flipped, setFlipped] = useState(false);

  const toggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setFlipped(f => !f);
  };

  return (
    <div
      className={`ph-metric-flip ${tile.tone}${flipped ? ' is-flipped' : ''}`}
      data-hint={t('hintFindOutMore')}
      onClick={toggle}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') toggle(e);
      }}
      role="button"
      tabIndex={0}
      aria-pressed={flipped}
      aria-label={t('tile.aria', {
        label: tile.label,
        value: tile.value,
        action: flipped ? t('tile.hide') : t('tile.show'),
      })}
    >
      <div className="ph-metric-flip-inner">
        <div className="ph-metric-face ph-metric-front">
          <span className="ph-metric-ic">
            <Icon name={tile.icon} size={13} />
          </span>
          <span className="ph-metric-lab">{tile.label}</span>
          <span className="ph-metric-val">{tile.value}</span>
        </div>
        <div className="ph-metric-face ph-metric-back">
          <div className="ph-metric-back-scroll">
            <p className="ph-metric-detail">{tile.detail}</p>
            {tile.action ? (
              <p className="ph-metric-action">{tile.action}</p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Placeholder tiles while segment nuggets load off the critical path. */
export function MetricInsightTileSkeleton({ index }: { index: number }) {
  return (
    <div
      className="ph-metric-skel"
      style={{ animationDelay: `${index * 90}ms` }}
      aria-hidden
    >
      <span className="ph-metric-skel-ic" />
      <span className="ph-metric-skel-lab" />
      <span className="ph-metric-skel-val" />
      <span className="ph-metric-skel-bars" aria-hidden>
        <i style={{ '--h': '38%', '--d': '0ms' } as React.CSSProperties} />
        <i style={{ '--h': '72%', '--d': '120ms' } as React.CSSProperties} />
        <i style={{ '--h': '54%', '--d': '240ms' } as React.CSSProperties} />
        <i style={{ '--h': '88%', '--d': '360ms' } as React.CSSProperties} />
      </span>
      <span className="ph-metric-skel-shine" />
    </div>
  );
}
